/**
 * WHAT A HEALTH TILE MAY SAY ABOUT ITS READING.
 *
 * The verdict on each tile (ok or needs attention) is the monitoring feed's own, never a
 * threshold on the figure computed here. But a verdict is only as current as its reading:
 * System administration said "Within tolerance" on readings 57 days old (module walk,
 * 30 Oct 2026), a reassurance resting on nothing current.
 *
 * THE WINDOW. The feed records no interval of its own (each reading is a metric, a value and
 * when it was observed), so the window is set here: a feed that pages on-call publishes every
 * few minutes, and a reading older than fifteen minutes is past what that feed would let go
 * by. Past it, the tile says how old the last reading is, and stops vouching for it either way.
 */
export const READING_FRESH_FOR_MS = 15 * 60_000;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "16 minutes", "1 hour", "57 days": the age of a reading in words. */
export function ageWords(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return plural(hours, 'hour');
  return plural(Math.round(hours / 24), 'day');
}

export interface TileVerdict {
  tone: 'ok' | 'attention';
  /** The status dot's label, read by a screen reader. */
  label: string;
  /** Words shown after the metric's detail, so a sighted reader is told too. */
  note: string;
  /** Whether the tile is drawn as needing a look. */
  flagged: boolean;
}

/** The tile's verdict: the feed's own while the reading is current, how old it is once not. */
export function tileVerdict(ok: boolean, observedAt: string, now: number = Date.now()): TileVerdict {
  const age = now - new Date(observedAt).getTime();
  const stale = !(age <= READING_FRESH_FOR_MS);
  const attention = ok ? '' : ' · needs attention';
  if (!stale)
    return ok
      ? { tone: 'ok', label: 'Within tolerance', note: '', flagged: false }
      : { tone: 'attention', label: 'Needs attention', note: attention, flagged: true };
  return {
    tone: 'attention',
    label: 'Too old to trust',
    note: `${attention} · no current reading: the last is ${ageWords(age)} old`,
    flagged: true,
  };
}
